import {createBrowserRouter, RouterProvider} from 'react-router';
import {generateRoutes} from '../lib/router/routes.js';
import {routes} from './router.js';
import {App} from '../pages/_app.js';
import {useSessionStore} from '#stores/session/index.js';
import {StrictMode, useEffect, useMemo} from 'react';
import {createRoot} from 'react-dom/client';
import {safeJsonParse} from '#utils/strings.js';
import type {Session} from '#types/session.js';
import type {Nullable} from '#types';

type RootProps = {
  props: {
    [k: `/${string}`]: Record<string, unknown>;
    session: Nullable<Session>;
  };
};

function Root({props: {session, ...props}}: RootProps) {
  const {setSession} = useSessionStore();
  const router = useMemo(() => {
    const children = generateRoutes(routes, props);
    return createBrowserRouter([
      {
        path: '/',
        element: <App />,
        children,
      },
    ]);
  }, [props]);

  useEffect(() => {
    setSession(session);
  }, [session, setSession]);

  return <RouterProvider router={router} />;
}

function getInitialProps(domNode: HTMLElement | null): RootProps['props'] {
  if (!domNode) {
    return {session: null};
  }

  const dataProps = domNode.getAttribute('data-props');
  const parsedProps = safeJsonParse(dataProps, {});

  return {
    session: null,
    ...parsedProps,
  };
}

function init() {
  const mountPoint = document.getElementById('react');

  if (!mountPoint) {
    console.error('React mount point not found');
    return;
  }

  const initialProps = getInitialProps(mountPoint);
  const root = createRoot(mountPoint);

  root.render(
    <StrictMode>
      <Root props={initialProps} />
    </StrictMode>
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

export {Root, getInitialProps};
